import { useState } from "react";
import { TrendingDown, TrendingUp } from "lucide-react";
import type { Bias } from "../../hooks/useActiveCycle";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface BiasSelectorProps {
  onStart: (bias: Bias) => void;
  pending: boolean;
  /** When true, the BUY/SELL buttons are kept visible but disabled (e.g. market not TRADEABLE). */
  disabled?: boolean;
}

/**
 * BUY/SELL bias selector with an explicit confirmation step
 * (SPEC §14: "BUY/SELL bias buttons + confirmation modal").
 * Click bias → click again to confirm → onStart fires. Choosing
 * the other bias before confirming swaps the pending selection.
 * {@code disabled} greys the buttons out (kept visible) when a cycle
 * cannot currently be launched — e.g. the market is not TRADEABLE.
 */
export default function BiasSelector({ onStart, pending, disabled = false }: BiasSelectorProps) {
  const [pendingBias, setPendingBias] = useState<Bias | null>(null);

  if (pendingBias) {
    return (
      <div
        className="bias-confirm flex flex-col gap-3"
        role="group"
        aria-label="Confirmer le démarrage du cycle"
      >
        <p className="text-muted-foreground text-sm">
          Démarrer un nouveau cycle avec le biais{" "}
          <strong className={pendingBias === "BUY" ? "text-buy" : "text-sell"}>
            {pendingBias}
          </strong>
          {" "}?
        </p>
        <div className="bias-confirm__actions flex gap-2">
          <Button
            type="button"
            onClick={() => onStart(pendingBias)}
            disabled={pending}
            className={cn(
              "flex-1 text-white",
              pendingBias === "BUY"
                ? "bias-confirm__btn--buy bg-buy hover:bg-buy/90"
                : "bias-confirm__btn--sell bg-sell hover:bg-sell/90",
            )}
          >
            {pending ? "Démarrage…" : `Confirmer ${pendingBias}`}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setPendingBias(null)}
            disabled={pending}
          >
            Annuler
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="bias-selector grid grid-cols-2 gap-2"
      role="group"
      aria-label="Choisir le biais du cycle"
    >
      <button
        type="button"
        className="bias-btn bias-btn--buy border-buy/40 bg-buy-soft text-buy hover:bg-buy/20 flex items-center justify-center gap-2 rounded-lg border py-3 text-base font-bold transition-colors disabled:opacity-50"
        onClick={() => setPendingBias("BUY")}
        disabled={pending || disabled}
      >
        <TrendingUp className="size-5" aria-hidden />
        BUY
      </button>
      <button
        type="button"
        className="bias-btn bias-btn--sell border-sell/40 bg-sell-soft text-sell hover:bg-sell/20 flex items-center justify-center gap-2 rounded-lg border py-3 text-base font-bold transition-colors disabled:opacity-50"
        onClick={() => setPendingBias("SELL")}
        disabled={pending || disabled}
      >
        <TrendingDown className="size-5" aria-hidden />
        SELL
      </button>
    </div>
  );
}
